import React, { useContext } from "react";
import { ModuleDashboard } from "../../../../../../interfaces/Dashboard";
import ResourceCard from "../../../../../../design-system-components/ResourceCard/ResourceCard";
import cn from "classnames";
import useClassroom from "../../useClassroom";
import { defineMessages, useIntl } from "react-intl";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { sessionStore } from "../../../../../../contexts/SessionContext";
import { ThemeType } from "../../../../../../interfaces/Theme";
import { useData } from "../../../../../../hooks/useData";
import { ClickAction } from "../../../../../../design-system-components/Card/types";

import "./ModuleList.scss";
import { useCachedStudentsAlerts } from "../../../ProgressionOverview/useStudentsAlerts";
import { getBlockedStudentCountByModule } from "../../../api/utils";
import { getResourceIndex } from "../../../../../../utils/dataRetrieval";
import { Spinner } from "../../../../../../components/Loader/Loader";

type Props = {
    type: "group" | "classroom";
    modules?: ModuleDashboard[];
    students: string[];
    collectionId: string;
    className?: string;
};

const ModuleList = ({
    type,
    modules,
    collectionId,
    students,
    className,
}: Props) => {
    const data = useData();
    const { getUrl } = useClassroom();
    const intl = useIntl();

    if (module === undefined) {
        return (
            <Spinner baseClass="evb-spinner__container evb-spinner--animate inline_spinner" />
        );
    }

    return (
        <ul className={cn("module-list", className)}>
            {data.modules.map((module) => {
                const dashboardInfo = modules?.find((m) => m.id === module.id);

                if (!dashboardInfo) return null;
                return (
                    <li key={module.id}>
                        <Module
                            data={dashboardInfo}
                            title={`${getResourceIndex(
                                module,
                                data,
                                "module"
                            )}. ${module.title.short ?? module.title.long}`}
                            clickAction={{
                                altTxt: intl.formatMessage(
                                    messages.a11yNavigateToProgressionOverview,
                                    {
                                        moduleTitle:
                                            module.title.short ??
                                            module.title.long,
                                    }
                                ),
                                to: getUrl(
                                    `${type}ProgressionOverview`,
                                    collectionId,
                                    module.id
                                ),
                                type: "internalRedirection",
                            }}
                            students={students}
                        />
                    </li>
                );
            })}
        </ul>
    );
};

export default ModuleList;

const Module = ({
    data,
    title,
    clickAction,
    students,
}: {
    data: ModuleDashboard;
    title: string;
    clickAction: ClickAction;
    students: string[];
}) => {
    const intl = useIntl();
    const {
        session: { theme },
    } = useContext(sessionStore);

    const studentsAlerts = useCachedStudentsAlerts();
    const blockedStudentCount = studentsAlerts?.length
        ? getBlockedStudentCountByModule(
              data?.id,
              students.map((id) => id),
              studentsAlerts
          )
        : 0;
    if (!data) return null;

    return (
        <ResourceCard
            moduleId={data.id}
            className={cn("module-list-item", {})}
            name={{
                as: "h3",
                content: (
                    <span className="module-list-item__title">
                        {`${title}`}
                        {!data.active && <Icon path="lock" />}
                    </span>
                ),
            }}
            mainChip={
                data.progression === 100
                    ? {
                          colorMode: theme,
                          input: (
                              <span className="module-list-item__chip">
                                  <span>
                                      {intl.formatMessage(messages.finished)}
                                  </span>
                                  <Icon path="checkmark" size="small" />
                              </span>
                          ),
                      }
                    : undefined
            }
            icon={{
                path: "icon_mode_solo_ai",
                color: "var(--element-teacher-default)",
            }}
            progression={{
                value: data.progression,
                valueLabel:
                    data.progression > 0
                        ? `${intl.formatMessage(
                              messages.averageProgression
                          )} : ${data.progression}%`
                        : intl.formatMessage(messages.noProgressionForModule),
                colors: {
                    foreground: "var(--element-teacher-default)",
                    background:
                        theme === ThemeType.Dark
                            ? "var(--white-15)"
                            : "var(--block-item2-default)",
                },
            }}
            rightIcon={{
                path: "chevron_right",
                size: "medium",
            }}
            clickAction={{
                ...clickAction,
                disabled: data.progression === 0,
            }}
            noCacheButton={true}
            blockedAlert={blockedStudentCount}
        />
    );
};

const messages = defineMessages({
    averageProgression: {
        id: "module-list-averageProgression",
        defaultMessage: "Average progression",
    },
    noProgressionForModule: {
        id: "dashboard-noProgression",
        defaultMessage: "There is no progression for this module",
    },
    finished: {
        id: "module-list-finished",
        defaultMessage: "Finished",
    },
    a11yNavigateToProgressionOverview: {
        id: "module-list-a11y-navigateToProgressionOverview",
        defaultMessage: "See progression overview for module {moduleTitle}",
    },
});
